"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = PayRateChangeThreshold;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_i18next_1 = require("react-i18next");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const react_native_paper_1 = require("react-native-paper");
const Button_1 = __importDefault(require("../components/Button"));
const CompanySettings_1 = __importDefault(require("../api/CompanySettings"));
const native_1 = require("@react-navigation/native");
const ModalWrapper_1 = __importDefault(require("../components/ModalWrapper"));
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
const UserContext_1 = require("../contexts/UserContext");
const async_storage_1 = __importDefault(require("@react-native-async-storage/async-storage"));
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
function PayRateChangeThreshold(props) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const route = (0, native_1.useRoute)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    //const DEFAULT_THRESHOLD = 500_000;
    //const initial = route.params?.current ?? DEFAULT_THRESHOLD;
    //const [original, setOriginal] = React.useState<string>(String(initial));
    //const [raw, setRaw] = React.useState<string>(String(initial));
    const initialRaw = route.params?.current == null ? "" : String(route.params.current);
    const [original, setOriginal] = react_1.default.useState(initialRaw);
    const [raw, setRaw] = react_1.default.useState(initialRaw);
    const [error, setError] = react_1.default.useState(null);
    const [loading, setLoading] = react_1.default.useState(true);
    const [saving, setSaving] = react_1.default.useState(false);
    const formatWithCommas = (s) => !s ? "" : (isFinite(Number(s)) ? new Intl.NumberFormat('en-US').format(Number(s)) : "");
    react_1.default.useEffect(() => {
        let mounted = true;
        if (route.params?.current !== undefined) {
            setLoading(false);
            return () => { mounted = false; };
        }
        (async () => {
            try {
                const token = await userContext.getToken();
                const apiValue = await CompanySettings_1.default.getPayThreshold(token);
                const effective = apiValue ?? 500_000;
                if (mounted) {
                    const str = String(effective);
                    setOriginal(str);
                    setRaw(str);
                    setError(null);
                }
            }
            finally {
                if (mounted)
                    setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [userContext, route.params?.current]);
    const validate = (digits) => {
        if (digits === "")
            return null;
        const n = Number(digits);
        if (!Number.isFinite(n) || n <= 0)
            return t('company:text.invalidPayRate');
        if (!Number.isSafeInteger(n))
            return t('company:text.invalidPayRateLarge');
        return null;
    };
    const onChange = (v) => {
        const digits = v.replace(/[^\d]/g, '');
        setRaw(digits);
        setError(validate(digits));
    };
    const changed = raw !== original;
    const canSave = !loading && !saving && !error && changed;
    const onSave = async () => {
        const err = validate(raw);
        setError(err);
        if (err)
            return;
        try {
            setSaving(true);
            const token = await userContext.getToken();
            const payload = raw === "" ? null : Number(raw);
            await CompanySettings_1.default.setPayThreshold(token, payload);
            await async_storage_1.default.setItem(`payThreshold:${userContext.clientId}`, JSON.stringify(payload));
            react_native_1.DeviceEventEmitter.emit('event.showSnackbar', {
                type: 'success',
                message: t('company:messages.savedPayRateThreshold') ?? 'Saved',
            });
            react_native_1.DeviceEventEmitter.emit('event.payThresholdUpdated', payload);
            props.navigation.goBack();
        }
        catch {
            react_native_1.DeviceEventEmitter.emit('event.showSnackbar', {
                type: 'error',
                message: t('company:messages.payRateThresholdFailed') ?? 'Could not save. Try again.',
            });
        }
        finally {
            setSaving(false);
        }
    };
    return ((0, jsx_runtime_1.jsxs)(ModalWrapper_1.default, { navigation: props.navigation, needsConfirmation: changed, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { paddingHorizontal: 12, paddingTop: 8, paddingBottom: 2 }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Pen, size: 20, color: THEME.Secondary }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontWeight: '500', fontSize: size != 'sm' ? 18 : 16, marginLeft: 12, flex: 1 }, children: t('company:labels.editAnnualPayRate') }), (0, jsx_runtime_1.jsx)(Button_1.default, { "aria-label": t('common:buttons.close'), hitSlop: 12, type: "borderless", onPress: () => props.navigation.goBack(), children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.Xmark, size: 20, color: THEME.Text, style: { margin: 4 } }) })] }) }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { paddingHorizontal: 12, paddingBottom: 12 }, children: [(0, jsx_runtime_1.jsx)(react_native_paper_1.TextInput, { label: t('company:labels.payRate'), mode: "outlined", dense: true, inputMode: "numeric", keyboardType: react_native_1.Platform.select({ ios: 'number-pad', android: 'numeric', default: 'numeric' }), value: formatWithCommas(raw), onChangeText: onChange, error: !!error, left: (0, jsx_runtime_1.jsx)(react_native_paper_1.TextInput.Affix, { text: "$" }) }), !!error && (0, jsx_runtime_1.jsx)(react_native_paper_1.HelperText, { type: "error", visible: true, children: error }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignItems: 'center', backgroundColor: THEME.UltraLightGray, paddingVertical: 8, paddingHorizontal: 10, borderRadius: 4, marginTop: 8 }, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.CircleQuestion, size: 14, color: THEME.TextMuted, style: { marginRight: 8 } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.TextMuted, fontSize: 12, flex: 1 }, children: t('company:text.paySettingsHint') })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { marginTop: 18, flexDirection: 'row', marginHorizontal: -6 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1, marginHorizontal: 6 }, children: (0, jsx_runtime_1.jsx)(Button_1.default, { type: "outline", text: t('common:buttons.cancel'), onPress: () => props.navigation.goBack() }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flex: 1, marginHorizontal: 6 }, children: (0, jsx_runtime_1.jsx)(Button_1.default, { text: t('common:buttons.save'), disabled: !canSave || saving, onPress: onSave }) })] })] })] }));
}
